import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { OnboardingProgress } from './shared';
import Button from './shared/Button';

// dismissHint key for the Getting started row (server-backed, per account;
// Settings → Help → "Reset Hints" brings it back)
const CHECKLIST_HINT_KEY = 'onboardingChecklist';

/**
 * Activation bands: the "let's get started" banner and the Getting Started
 * checklist.
 *
 * These lived on the profile until 2026-09-13. Kurtis moved them to Home
 * because that is where they are actually seen — on an empty profile the
 * checklist alone ran ~800px, pushing the travel map (the band the profile
 * exists for) off the first two screenfuls, to advertise work that has
 * nothing to do with where you have travelled.
 *
 * Both bands self-retire, so this renders nothing for an established account.
 * The checklist is one row since 2026-10-01 (it was the full seven-step list,
 * ~750px on a phone with no close button). It shows in an account's first
 * 30 days until it's finished or dismissed. It used to retire after three
 * milestones of ANY kind, but pages now record a milestone on first visit,
 * so tapping through three tabs would have hidden it with nothing set up.
 * The auth gate is explicit rather than inherited: the profile versions were
 * gated on `ownerMode`, which implied a signed-in owner. Home serves anonymous
 * visitors too, and `hasCompletedOnboarding` is false-y while loading AND for
 * a logged-out reader — without this gate a stranger would be invited to
 * finish onboarding they never started.
 */
function OnboardingActivation() {
  const navigate = useNavigate();
  const { t } = useTranslation('onboarding');
  const { currentUser } = useAuth();
  const { skipOnboarding, hasCompletedOnboarding, isLoading, isNewAccount, hasSeenHint, dismissHint } = useOnboarding();

  if (!currentUser) return null;

  const showBanner = !isLoading && !hasCompletedOnboarding;
  // isNewAccount is null until known; null must not read as new. The row
  // retires itself once every step is done.
  const showChecklist = !isLoading && isNewAccount === true && !hasSeenHint(CHECKLIST_HINT_KEY);
  if (!showBanner && !showChecklist) return null;

  return (
    <>
      {showBanner && (
        // A pale tinted panel, not a gradient block (2026-10-01 refresh)
        <section className="bg-primary-lighter rounded-card p-6">
          <div className="text-center">
            <h2 className="font-display font-medium text-2xl text-content-primary mb-2">{t('activation.heading')}</h2>
            <p className="text-content-secondary mb-6">{t('activation.subheading')}</p>
            {/* returnTo is Home, not the profile — this band's home moved with
                it, and sending someone back to a page they were not on is the
                kind of small lie that makes a flow feel broken. */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => navigate('/welcome?returnTo=/')}>
                {t('activation.quickSetupButton')}
              </Button>
              <Button variant="ghost" onClick={skipOnboarding}>
                {t('activation.skipButton')}
              </Button>
            </div>
          </div>
        </section>
      )}

      {showChecklist && (
        <OnboardingProgress variant="row" onDismiss={() => dismissHint(CHECKLIST_HINT_KEY)} />
      )}
    </>
  );
}

export default OnboardingActivation;
